"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, ShieldCheck, MapPin, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
export default function MarylandHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: shouldReduceMotion ? 0 : 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const keyFacts = [{
    id: 1,
    icon: Clock,
    label: "24/7 on-call dispatch"
  }, {
    id: 2,
    icon: MapPin,
    label: "Direct local crews, no call centers"
  }, {
    id: 3,
    icon: ShieldCheck,
    label: "Insurance billing coordination"
  }];
  return <section id="marylandhero" data-nav="dark" className="relative overflow-hidden bg-secondary text-secondary-foreground py-20 sm:py-28 md:py-32">
      {/* Decorative dark background overlay with subtle ambient tint */}
      <div className="absolute inset-0 bg-gradient-to-b from-secondary/95 via-secondary to-secondary pointer-events-none z-0" />
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-primary/20 blur-3xl pointer-events-none z-0" />

      <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="max-w-3xl">
          {/* Tagline / Eyebrow */}
          <motion.div variants={itemVariants} className="mb-4">
            <Badge variant="outline" className="border-secondary-foreground/20 text-secondary-foreground bg-secondary/60 px-3 py-1 text-xs sm:text-sm font-medium">
              Maryland Emergency Response
            </Badge>
          </motion.div>

          {/* Primary H1 */}
          <motion.h1 variants={itemVariants} className="font-default text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-secondary-foreground mb-6">
            Restoration Services in Maryland
          </motion.h1>

          {/* Subheadline */}
          <motion.p variants={itemVariants} className="text-lg sm:text-xl text-secondary-foreground/80 leading-relaxed mb-8 max-w-2xl">
            24/7 local emergency response across Maryland. Direct crews arrive
            equipped for water, fire, mold, and storm damage to secure your
            property immediately.
          </motion.p>

          {/* CTAs */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 mb-12">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-md">
              <Link to="/ContactRequestServicePage" className="inline-flex items-center gap-2">
                Request emergency service
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild size="lg" variant="outline" className="border-secondary-foreground/30 text-secondary-foreground hover:bg-secondary-foreground/10">
              <a href="tel:8005550199" className="inline-flex items-center gap-2">
                <Phone className="size-4" />
                <span>Call 24/7 dispatch</span>
              </a>
            </Button>
          </motion.div>

          {/* Highlight badges / key facts */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-secondary-foreground/15">
            {keyFacts.map((fact, index) => {
            const IconComponent = fact.icon;
            return <div key={fact.id} data-index={index} className="flex items-center gap-3 text-sm text-secondary-foreground/85">
                  <div className="flex items-center justify-center size-8 rounded-md bg-secondary-foreground/10 text-accent">
                    <IconComponent className="size-4" />
                  </div>
                  <span>{fact.label}</span>
                </div>;
          })}
          </motion.div>
        </motion.div>
      </div>
    </section>;
}